import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { FiChecklist2AuditListComponent } from '@fi-shared/checklist/fi-checklist2-audit/fi-checklist2-audit-list/fi-checklist2-audit-list.component';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiChecklist2EditComponent } from '@fi-shared/checklist/fi-checklist2-edit/fi-checklist2-edit.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType, ModuleType, ObjectTypes } from '@fi-shared/types';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { ChecklistV2ListAuditNavigationTabsComponent } from '@fi-app/checklist/checklist-v2/checklist-v2-list-audit-navigation-tabs/checklist-v2-list-audit-navigation-tabs.component';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import {
    checklistIdsResolveFn,
    ChecklistV2Edit_checklist,
    ChecklistV2Edit_isChatAvailable,
    ChecklistV2EditCheckpointSections_accessRightFilterCollection,
    ChecklistV2View_businessUnitId,
    ChecklistV2View_fromLinkTo,
    ChecklistV2View_isChecklistTimeOut,
    ChecklistV2View_isPreviewMode,
    ChecklistV2ViewState_checklistUnitsSchedulePeriods,
    ChecklistV2ViewState_checklistViewer,
    ChecklistV2ViewState_checklistViewerByChecklist,
    ChecklistV2ViewState_checklistViewerByChecklistTemplate,
} from '@fi-app/checklist/checklist-v2/checklist-v2.states';
import { FiChecklist2ViewerComponent } from '@fi-shared/checklist/fi-checklist2-viewer/fi-checklist2-viewer.component';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { FiChecklist2CheckpointsSectionEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoints-section-edit.component';
import { FiCheckpointEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoint-edit/checkpoint-edit.component';
import {
    ChecklistV2Add_checklistFn,
    ChecklistV2_EditCheckpointResolve,
    ChecklistV2EditCheckpointSectionsState_handleChecklistFn,
    ChecklistV2_EditCheckpointSectionsResolve,
    onChecklistEditorStateExit,
} from '@fi-app/checklist/checklist-v2/checklist-v2-shared-resolvers';
import { FiChecklistEditorStoreService } from '@fi-shared/checklist/fi-checklist-editor-store.service';

const ChecklistV2AuditState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit._name,
    url: '/Checklist2Audit',
    abstract: true,
    component: ChecklistV2ListAuditNavigationTabsComponent,
};

const ChecklistV2AuditListState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    params: {
        ids: null,
    },
    component: FiChecklist2AuditListComponent,
    reloadOnSearch: false,
    resolve: {
        ids: checklistIdsResolveFn,
    },
};

const ChecklistV2AuditAddState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Add._name,
    url: '/Add',
    params: {
        checklist: null,
        isAudit: true,
        isTemplateBased: false,
        templateMode: false,
    },
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    resolve: {
        accessRightFilterCollection: [AccessRightService, ChecklistV2AuditEdit_accessRightFilterCollection],
        savedQuickSelectionFilters: [AccessRightService, ChecklistV2AuditEdit_savedQuickSelectionFiltersResolveFn],
        auditMode: [() => true],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
        checklist: ChecklistV2Add_checklistFn,
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2AuditEditState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Edit._name,
    url: '/{id:[0-9]+}/Edit',
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    params: {
        isAudit: true,
        isTemplateBased: false,
        templateMode: false,
    },
    resolve: {
        checklist: [
            FiStateService,
            Transition,
            FiChecklist2Service,
            FiCheckpoint2HelperService,
            FiChecklistEditorStoreService,
            ChecklistV2Edit_checklist,
        ],
        accessRightFilterCollection: [AccessRightService, ChecklistV2AuditEdit_accessRightFilterCollection],
        savedQuickSelectionFilters: [AccessRightService, ChecklistV2AuditEdit_savedQuickSelectionFiltersResolveFn],
        auditMode: [() => true],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2AuditEditCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Edit.EditCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: true,
        templateMode: false,
        isTemplateBased: false,
    },
    resolve: ChecklistV2_EditCheckpointSectionsResolve,
};

const ChecklistV2AuditEditCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Edit.EditCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: true,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

const ChecklistV2AuditAddCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Add.AddCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: true,
        templateMode: false,
        isTemplateBased: false,
    },
    resolve: {
        accessRightFilterCollection: [
            FiStateService,
            Transition,
            AccessRightService,
            ChecklistV2EditCheckpointSections_accessRightFilterCollection,
        ],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
        handleChecklist: ChecklistV2EditCheckpointSectionsState_handleChecklistFn,
    },
};

const ChecklistV2AuditAddCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.Add.AddCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: true,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

const ChecklistV2AuditViewState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.View,
    url: '/{id:[0-9]+}/View?{:businessUnitId}&{:checklistPeriodId}&{:isPreviewMode}&{:fromLinkTo}',
    params: {
        isChecklistTimeOut: null,
    },
    views: {
        '@Module': {
            component: FiChecklist2ViewerComponent,
        },
    },
    resolve: {
        checklistViewer: ChecklistV2ViewState_checklistViewer,
        checklistUnitsSchedulePeriods: ChecklistV2ViewState_checklistUnitsSchedulePeriods,
        businessUnitId: ChecklistV2View_businessUnitId,
        isChecklistTimeOut: ChecklistV2View_isChecklistTimeOut,
        isPreviewMode: ChecklistV2View_isPreviewMode,
        fromLinkTo: ChecklistV2View_fromLinkTo,
        auditMode: [() => true],
    },
};

const ChecklistV2AuditViewByChecklistState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.ViewByChecklist,
    url: '/{id:[0-9]+}/ViewByChecklist?{:fromLinkTo}',
    views: {
        '@Module': {
            component: FiChecklist2ViewerComponent,
        },
    },
    resolve: {
        checklistViewer: ChecklistV2ViewState_checklistViewerByChecklist,
        fromLinkTo: ChecklistV2View_fromLinkTo,
        auditMode: [() => true],
    },
};

const ChecklistV2AuditViewByChecklistTemplateState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2Audit.ViewByChecklistTemplate,
    url: '/{id:[0-9]+}/ViewByChecklistTemplate',
    views: {
        '@Module': {
            component: FiChecklist2ViewerComponent,
        },
    },
    resolve: {
        checklistViewer: ChecklistV2ViewState_checklistViewerByChecklistTemplate,
        auditMode: [() => true],
    },
};

export function ChecklistV2AuditEdit_accessRightFilterCollection(accessRightService: AccessRightService) {
    return accessRightService.getAccessRightsForPublicationByObjectType(ObjectTypes.Checklist2Audit);
}

export function ChecklistV2AuditEdit_savedQuickSelectionFiltersResolveFn(accessRightService: AccessRightService) {
    return accessRightService.GetSelectionFiltersForPublication(ModuleType.ChecklistV2Audit, true);
}

export const ChecklistV2AuditStates: StatesModule = {
    states: [
        ChecklistV2AuditState,
        ChecklistV2AuditListState,
        ChecklistV2AuditAddState,
        ChecklistV2AuditEditState,
        ChecklistV2AuditViewState,
        ChecklistV2AuditViewByChecklistState,
        ChecklistV2AuditEditCheckpointSectionsState,
        ChecklistV2AuditAddCheckpointSectionsState,
        ChecklistV2AuditEditCheckpointSectionsEditCheckpointState,
        ChecklistV2AuditAddCheckpointSectionsEditCheckpointState,
        ChecklistV2AuditViewByChecklistTemplateState,
    ],
};

export function registerChecklistV2AuditStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules(
        [FiStateNames.ChecklistV2Audit._name, FiStateNames.ChecklistV2Audit.List, FiStateNames.ChecklistV2Audit.View],
        {
            SettingType: [ApplicationSettingsType.ChecklistV2AuditLocal],
            AccessType: AccessRightType.Read,
        },
    );

    stateAccessCheckService.registerStateRules(
        [FiStateNames.ChecklistV2Audit.Template._name, FiStateNames.ChecklistV2Audit.Template.List],
        {
            SettingType: [ApplicationSettingsType.ChecklistV2AuditLocal],
            AccessType: AccessRightType.Admin,
        },
    );

    stateAccessCheckService.registerStateRules(
        [FiStateNames.ChecklistV2Audit.Schedule._name, FiStateNames.ChecklistV2Audit.Schedule.List],
        {
            SettingType: [ApplicationSettingsType.ChecklistV2AuditLocal],
            AccessType: AccessRightType.Share,
        },
    );
}
